<template>
  <div class="container-fluid bookings">
    <div class="fixed-top sticky-header">
      <b-col cols="12">
        <page-title>
          <span>{{ $t("_my_bookings.title") }}</span>

          <router-link :to="{ name: 'home_search' }">
            <button class="orange-white btn">
              {{ $t("booking.new") }}
            </button>
          </router-link>
        </page-title>
      </b-col>

      <filters
        v-show="isLoaded"
        ref="filters"
        :is-manager="isManager"
        @apply="applyFilters"
      />

      <div class="col-12 download-bookings" v-if="filters" v-show="isLoaded">
        <a :href="downloadSpreadsheetBookingsUrl" target="_blank">
          <span>{{ $t("booking.download_list") }}</span>

          <span class="btn orange-white">
            <i class="fas fa-file-download"></i>
          </span>
        </a>
      </div>

      <div class="row justify-content-center">
        <div v-if="displaySave" class="col-lg-8">
          <b-alert variant="warning" show>
            {{ $t("offline.draft.phrase") }}
          </b-alert>
        </div>
      </div>
    </div>

    <b-row v-if="!isLoaded" class="loader-section">
      <frontoffice-loader />
    </b-row>

    <div v-else>
      <b-modal id="modal-info" ok-only>
        {{ $t(confirmModalMessage) }}
      </b-modal>

      <div class="fold-mt">
        <multiselect
          v-if="isViewPortMobile"
          :options="tabOptions"
          :searchable="false"
          v-model="activeTab"
          track-by="tab"
          :allow-empty="false"
          :show-labels="false"
          :preselect-first="true"
          class="col-12 col-md-7 col-lg-5 p-0 choose-tabs"
          openDirection="bottom"
        >
          <template
            v-for="slotName in ['option', 'singleLabel']"
            :slot="slotName"
            slot-scope="data"
          >
            <tab-title
              :option="data.option"
              :nb="nbBookings(data.option.tab)"
            />
          </template>
        </multiselect>

        <b-tabs :class="tabsClass" v-model="tabIndex">
          <b-tab v-if="isManager" class="bookings-todo" ref="todo">
            <template v-if="isViewPortDesktop" slot="title">
              <tab-title :option="tabDetails.todo" :nb="nbBookings('todo')" />
            </template>

            <list
              tab-id="todo"
              :fields="getInProgressFields"
              :items="getTodoItems"
              @confirmAction="confirmAction"
            />
          </b-tab>

          <b-tab class="bookings-progress" ref="inProgress">
            <template v-if="isViewPortDesktop" slot="title">
              <tab-title
                :option="tabDetails.inProgress"
                :nb="nbBookings('inProgress')"
              />
            </template>

            <list
              tab-id="in_progress"
              :fields="getInProgressFields"
              :items="getInProgressItems"
              @confirmAction="confirmAction"
              :manage-pagination="false"
              @sortChanged="onSort('inProgress')"
            />

            <b-pagination
              v-if="pagination.inProgress.nbBookings"
              v-model="pagination.inProgress.currentPage"
              size="md"
              :total-rows="pagination.inProgress.nbBookings"
              :per-page="pagination.inProgress.perPage"
              :busy="!pagination.inProgress.loaded"
              aria-controls="in_progress"
              @input="setBookingListInProgress"
            />
          </b-tab>

          <b-tab class="bookings-archived" ref="history">
            <template v-if="isViewPortDesktop" slot="title">
              <tab-title
                :option="tabDetails.history"
                :nb="nbBookings('history')"
              />
            </template>

            <list
              tab-id="history"
              :fields="getHistoryFields"
              :items="getHistoryItems"
              :manage-pagination="false"
              @sortChanged="onSort('history')"
            />

            <b-pagination
              v-if="pagination.history.nbBookings"
              v-model="pagination.history.currentPage"
              size="md"
              :total-rows="pagination.history.nbBookings"
              :per-page="pagination.history.perPage"
              :busy="!pagination.history.loaded"
              aria-controls="history"
              @input="setBookingHistory"
            />

            <p v-else class="notfound">
              {{ $t("booking.notFoundMessage.history") }}
            </p>
          </b-tab>

          <b-tab v-if="isManager" @click="showStats" ref="stats">
            <template v-if="isViewPortDesktop" slot="title">
              <tab-title :option="tabDetails.stats" />
            </template>

            <tab-stats v-if="tabIndex==3"/>
          </b-tab>
        </b-tabs>
      </div>
    </div>
  </div>
</template>

<script>
import tabStats from "./bookings-tab-stats.vue";
import formatter from "@/components/global/mixins/formatterMethods";
import frontofficeLoader from "@/components/frontoffice/loader";
import pageTitle from "@/components/frontoffice/pageTitle";
import CommonApi from "@/api/commonApi";
import bTabs from "bootstrap-vue/es/components/tabs/tabs";
import bTab from "bootstrap-vue/es/components/tabs/tab";
import { status, getStatusTranslation } from "@/utils/bookingStatus";
import dateInput from "@/components/frontoffice/form/baseInput/date";
import bookingDeadline from "@/components/frontoffice/mixins/bookingDeadline";
import list from "@/components/frontoffice/Booking/list";
import tabTitle from "@/components/frontoffice/Booking/tabTitle";
import viewPort from "@/conf/viewPort";
import filters from "@/components/frontoffice/Booking/filters/filters";
import { formatDateForOnlineRequest } from "@/utils/date";

export default {
  components: {
    list,
    tabTitle,
    pageTitle,
    frontofficeLoader,
    bTabs,
    bTab,
    dateInput,
    tabStats,
    filters,
  },

  mixins: [formatter, bookingDeadline, viewPort],

  data() {
    return {
      defaultTab: 'inProgress',
      isLoaded: false,
      bookingList: {
        todo: [],
        inProgress: [],
        history: [],
      },
      isManager: false,
      commonApi: new CommonApi(),
      cancellationComment: null,
      outOfSelectKeys: ["allow", "deny", "confirm", "order_number"],
      pagination: {
        history: {
          perPage: 10,
          currentPage: 1,
          orderBy: "",
          order: "",
          nbBookings: null,
          loaded: false,
        },
        inProgress: {
          perPage: 10,
          currentPage: 1,
          orderBy: "",
          order: "",
          nbBookings: null,
          loaded: false,
        }
      },
      tabIndex: 0,
      tabDetails: {
        todo: {
          tab: "todo",
          text: "_my_bookings.to_do",
        },
        inProgress: {
          tab: "inProgress",
          text: "_my_bookings.in_progress",
        },
        history: {
          tab: "history",
          text: "booking.archived_bookings",
        },
        stats: {
          tab: "stats",
          text: "_my_bookings.stats",
        },
      },
      activeTab: null,
      initDates: {
        from: null,
        to: null,
      },
      confirmModalMessage: "",
      filters: null,
      downloadSpreadsheetBookingsUrl: null,
      displaySave: this.$route.params.displaySave,
      draft: 4
    };
  },

  methods: {
    confirmAction(message) {
      this.$bvModal.show("modal-info");
      this.confirmModalMessage = this.$t(message);
      this.setBookingList().then(() => {
        this.setBookingListInProgress()
      });
    },

    showStats: function (event) {
      this.$events.fire("showTabStats", true);
    },

    applyFilters(filters) {
      this.filters = filters;
      this.downloadSpreadsheetBookingsUrl =
        this.commonApi.downloadSpreadsheetBookingsUrl(this.filters);
      this.setNbBookings().then(() => {
        this.setBookingList().then(() => {
          this.setBookingListInProgress().then(() => {
            this.setBookingHistory();
          })
        });
      })
    },

    parseFilters(filters) {
      return {
        bookingRef: filters.bookingRef,
        type: filters.type,
        dates: {
          from: filters.dates.from
            ? formatDateForOnlineRequest(filters.dates.from)
            : null,
          to: filters.dates.to
            ? formatDateForOnlineRequest(filters.dates.to)
            : null,
        },
        city: filters.city,
        hotel: filters.hotel,
        traveller: filters.traveller,
        status: filters.status,
        ref: filters.ref,
        orderNumber: filters.orderNumber,
      };
    },

    async setBookingListInProgress() {
      await this.commonApi
        .reservationsListInProgress(
          this.pagination.inProgress.perPage,
          this.pagination.inProgress.currentPage,
          this.pagination.inProgress.orderBy,
          this.pagination.inProgress.order,
          this.parseFilters(this.filters)
        )
        .then((response) => {
          if (response.data.data && response.data.data.inProgress) {
            this.bookingList.inProgress =
              response.data.data.inProgress || [];
          } else {
            this.bookingList.inProgress = [];
          }
          this.isLoaded = true;
        })
    },

    setBookingList() {
      return new Promise((resolve, reject) => {
        this.commonApi
          .reservationsList(this.parseFilters(this.filters))
          .then((response) => {
            if (response.data.data && response.data.data.bookingList && response.data.data.bookingList.todo) {
              this.bookingList.todo =
                response.data.data.bookingList.todo;
            } else {
              this.bookingList.todo = [];
            }
            this.isLoaded = true;
          })
          .then(() => {
            resolve();
          });
      });
    },

    setNbBookings() {
      return new Promise((resolve, reject) => {
        this.commonApi
          .nbBookingsByTab(
            this.parseFilters(this.filters)
          )
          .then((response) => {
            this.pagination.inProgress.nbBookings = parseInt(
              response.data.data.totalBookingInProgress
            )
            this.isManager = response.data.data.isManager
          })
          .then(() => {
            resolve();
          });
      })
    },

    setBookingHistory() {
      this.commonApi
        .historyReservationsList(
          this.pagination.history.perPage,
          this.pagination.history.currentPage,
          this.pagination.history.orderBy,
          this.pagination.history.order,
          this.parseFilters(this.filters)
        )
        .then((response) => {
          if (response.data.data.history) {
            this.bookingList.history = response.data.data.history;
          } else this.bookingList.history = [];
          this.pagination.history.nbBookings = parseInt(
            response.data.data.nbBookings
          );
          this.pagination.history.loaded = true;
        });
    },

    onSort(tab,options) {
      this.pagination[tab].orderBy = options.sortBy;
      this.pagination[tab].order = options.sortDesc ? "DESC" : "ASC";

      switch (tab) {
        case 'inProgress':
          this.setBookingListInProgress()
          break;
        case 'history':
          this.setBookingHistory();
          break;
      }
    },

    formatTravellers(travellers) {
      if (window.innerWidth <= 1024 || window.innerHeight <= 768)
        travellers = travellers.replace(",", ",<br/>");
      return travellers;
    },

    getStatusClass(bookingStatus) {
      if (bookingStatus === status.CONFIRMED || status === status.FINISHED) {
        return "confirmed";
      } else if (bookingStatus === status.CANCELLED) {
        return "cancelled";
      } else {
        return "transitional";
      }
    },

    getItems(items) {
      let ret = [];

      for (let item of items) {
        let getHotel = function (booking) {
          if (booking.hotel) return booking.hotel.name;
          //if (booking.desired_hotel) return booking.desired_hotel.name
          return null;
        };

        let getCity = function (booking) {
          if (booking.hotel && booking.hotel.city) return booking.hotel.city;
          if (booking.desired_hotel && booking.desired_hotel.city)
            return booking.desired_hotel.city;
          if (booking.desired_location && booking.desired_location.city)
            return booking.desired_location.city;
          if (booking.desired_city) return booking.desired_city;
          return null;
        };

        let getLocation = function (booking) {
          //if (booking.desired_location) return booking.desired_location.name
          return null;
        };

        let deadlines = this.getDeadlines(item.booking);
        let items = {
          _showDetails: false,
          type: item.booking.is_online ? "Online" : (item.booking.is_contingent ? "Allotement" : "Offline"),
          ref: item.booking.ref,
          travellers: this.formatTravellers(item.booking.travellers),
          destination: {
            hotel: getHotel(item.booking),
            city: getCity(item.booking),
            location: getLocation(item.booking),
          },
          dates: {
            from: this.formatDate(item.booking.arrival_date),
            to: this.formatDate(item.booking.departure_date),
          },
          deadline: deadlines.cancellation
            ? deadlines.cancellation
            : deadlines.confirmation,
          status: {
            status: getStatusTranslation(item.booking.status, this.isManager),
            className: this.getStatusClass(item.booking.status),
          },
          actions: item,
          whoBooked: this.fullName(item.booking.company_contact, false),
        };

        if (this.orderNumber) items.orderNumber = item.booking.order_number;
        ret.push(items);
      }

      return ret;
    },

    getCity(value) {
      let ret = "";

      if (value.hotel) {
        if (value.hotel.city) ret = value.hotel.city;
      } else if (value.city) {
        ret = value.city;
      }
      return ret;
    },

    getDefaultFields() {
      let ret = [
        {
          key: "travellers",
          label: this.$t("booking.array.travellers"),
          sortable: true,
        },
        {
          key: "dates",
          label: this.$t("booking.array.dates"),
          sortable: true,
        },
      ];

      let destination = {
        key: "destination",
        label: this.$t("booking.array.destination"),
        sortable: true,
      };

      if (this.isViewPortMobile) {
        ret.splice(0, 0, "show_details");

        if (this.isViewPortMobileMedium || this.isViewPortMobileLarge) {
          ret.splice(-1, 0, destination);
        }
      } else {
        ret.splice(0, 0, {
          key: "type",
          label: this.$t("booking.array.type"),
          sortable: true,
        });

        ret.splice(2, 0, destination);
      }

      return ret;
    },

    nbBookings(tab) {
      switch (tab) {
        case "todo":
          return this.bookingList[tab].length;
        case "inProgress":
          return this.pagination.inProgress.nbBookings;
        case "history":
          return this.pagination.history.nbBookings;
        case "stats":
          return false;
      }
    },
  },

  computed: {
    tabOptions() {
      let ret = [this.tabDetails.inProgress, this.tabDetails.history];

      if (this.isManager) {
        ret.splice(0, 0, this.tabDetails.todo);
        ret.splice(3, 0, this.tabDetails.stats);
      }

      return ret;
    },

    nbHistoryBookings() {
      return this.pagination.history.nbBookings;
    },

    getInProgressFields() {
      let ret = this.getDefaultFields();

      if (!this.isViewPortMobile)
        ret.push({
          key: "deadline",
          label: this.$t("booking.array.deadline"),
          sortable: true,
        });

      if (this.isViewPortMobileLarge || this.isViewPortDesktop) {
        ret.push({
          key: "status",
          label: this.$t("booking.array.status"),
          sortable: true,
        });

        if (this.orderNumber)
          ret.push({
            key: "orderNumber",
            label:
              this.orderNumber.label || this.$t("company.orderNumber.title"),
            sortable: true,
          });
      }

      ret.push({
        key: "actions",
        label: this.$t("booking.array.actions"),
        sortable: false,
        formatter: (value) => {
          let inSelect = [];
          let outOfSelect = {};

          for (let key in value.actions) {
            if (value.actions.hasOwnProperty(key)) {
              let display = value.actions[key];

              if (display) {
                if (
                  !this.isViewPortMobile &&
                  this.outOfSelectKeys.indexOf(key) !== -1
                ) {
                  outOfSelect[key] = {
                    name: key,
                    booking: value.booking,
                  };
                } else {
                  var trad = key
                  if (value.booking.status === this.draft && key === 'edit') {
                    trad = 'edit_draft'
                  }
                  inSelect.push({
                    name: key,
                    label: this.$t('booking.select.' + trad),
                    booking: value.booking
                  })
                }
              }
            }
          }

          // No need to set a select if there is only one more item, so there will be an extra button instead
          if (inSelect.length < 2) {
            for (let i in inSelect) {
              if (inSelect.hasOwnProperty(i)) {
                let item = inSelect[i];

                outOfSelect[item.name] = {
                  name: item.name,
                  booking: item.booking,
                };
              }
            }
          }

          return {
            in: inSelect,
            out: outOfSelect,
          };
        },
      });

      return ret;
    },

    getHistoryFields() {
      let ret = this.getDefaultFields();

      if (!this.isViewPortMobile)
        ret.push({
          key: "whoBooked",
          label: this.$t("booking.array.who_booked"),
          sortable: true,
        });

      if (this.isViewPortMobileLarge || this.isViewPortDesktop) {
        ret.push({
          key: "status",
          label: this.$t("booking.array.status"),
          sortable: true,
        });
      }

      ret.push({
        key: "actions",
        label: this.$t("booking.array.actions"),
        sortable: false,
      });

      return ret;
    },

    getTodoItems() {
      return this.getItems(this.bookingList.todo);
    },

    getInProgressItems() {
      return this.getItems(this.bookingList.inProgress);
    },

    getHistoryItems() {
      return this.getItems(this.bookingList.history);
    },

    tabsClass() {
      let ret = [];

      if (!this.isManager) ret.push("small-mt");
      if (this.isViewPortMobile) ret.push("undisplay-tab-controls");

      return ret.join(" ");
    },

    orderNumber() {
      let orderNumber = this.$store.getters["security/company"].orderNumber;

      return orderNumber !== null && orderNumber.required ? orderNumber : null;
    },
  },

  watch: {
    activeTab(val) {
      this.$refs[val.tab].activate();
    },
  },
};
</script>

<style lang="scss">
.bookings {
  .actions-btn {
    .multiselect.multiselect--above {
      z-index: 999999999999;
    }
  }
}
</style>
